import { z } from 'zod';

import { calendarDateSchema } from './employee';
import { wallClockTimeSchema } from './job';
import { RECORD_SORTS, listQuerySchema, sortParam } from './pagination';

/*
 * Job requests — reference/data-model.md §4, business-rules.md §6,
 * ROADMAP.md §5.4 and §5.5.
 *
 * **One word for the state: `declined`.** Not "rejected", not "Not Approved".
 * The prototype uses three for one state — the admin pills and the client badge
 * say "Declined", the client's notification label says "Request Not Approved",
 * and the archived roadmap says "rejected" (P11, P18). The labels below are the
 * only mapping, and the notification label comes from the same place.
 */

export const JOB_REQUEST_STATUS_VALUES = ['pending', 'approved', 'declined'] as const;
export type JobRequestStatusValue = (typeof JOB_REQUEST_STATUS_VALUES)[number];

/** The display names. A raw status must never reach a screen (P5). */
export const JOB_REQUEST_STATUS_LABELS: Record<JobRequestStatusValue, string> = {
  pending: 'Pending',
  approved: 'Approved',
  declined: 'Declined',
};

/**
 * Hours in, minutes stored.
 *
 * The client's form asks for hours because that is how somebody thinks about a
 * clean ("about two and a half"), and `job.estimated_duration_minutes` is
 * minutes because that is how the schedule works. One conversion, here, so the
 * browser and the server round the same way — 2.5 is 150 and 1.33 is 80, not 79
 * on one side and 80 on the other.
 */
export function hoursToMinutes(hours: number): number {
  return Math.round(hours * 60);
}

export function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}

const optionalNote = z
  .string()
  .trim()
  .max(2000, 'Must be 2000 characters or fewer')
  .transform((value) => (value.length === 0 ? null : value))
  .nullable()
  .optional();

/**
 * What a client submits.
 *
 * Everything except the property is optional, and that is the point: a request
 * is what somebody *asked for*, not a booking. Somebody who does not know which
 * service they want, or when, still has a request worth receiving — the office
 * rings them. Making the form strict would turn those into phone calls, which is
 * the thing this screen exists to reduce.
 *
 * No `clientId` and no `categoryId`. The client comes from the session, and
 * which checklist a property is cleaned to is the office's decision — it is what
 * a cleaner is judged against.
 */
export const createJobRequestSchema = z.object({
  propertyId: z.uuid('Choose which address needs cleaning'),

  serviceTypeId: z.uuid().nullable().optional(),

  preferredDate: calendarDateSchema.nullable().optional(),
  preferredTime: wallClockTimeSchema.nullable().optional(),

  /**
   * Optional — "leave blank if unsure", exactly as the prototype puts it
   * (FINDINGS.md §10.3). Quarter-hour floor so "0.1 hours" cannot become a
   * six-minute clean.
   */
  estimatedDurationHours: z
    .number()
    .min(0.25, 'Give at least a quarter of an hour, or leave it blank')
    .max(24, 'A clean cannot run longer than a day')
    .nullable()
    .optional(),

  notes: optionalNote,
});

export type CreateJobRequestInput = z.infer<typeof createJobRequestSchema>;

export const clientJobRequestListQuerySchema = listQuerySchema.extend({
  status: z.enum([...JOB_REQUEST_STATUS_VALUES, 'all']).default('all'),
});

export type ClientJobRequestListQuery = z.infer<typeof clientJobRequestListQuerySchema>;

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

/** What the client sees of their own request. */
export interface ClientJobRequestView {
  id: string;

  status: JobRequestStatusValue;
  statusLabel: string;

  /** The nickname if they gave one, otherwise the address. Never an id. */
  propertyLabel: string | null;
  /** The display name, never a slug — and the same name everywhere (P17). */
  serviceTypeName: string | null;

  preferredDate: string | null;
  preferredTime: string | null;
  estimatedDurationMinutes: number | null;

  notes: string | null;

  /**
   * The office's note back — **shown on an approval as well as a decline.**
   * That is why this is `review_note` and not `decline_reason` (P16): two of
   * the four declines in the prototype reach the client with nothing at all.
   */
  reviewNote: string | null;

  submittedAt: string;
  reviewedAt: string | null;

  /** Set once approved, so the client can open the clean it became. */
  createdJobId: string | null;
  createdJobNumber: string | null;
}

// ---------------------------------------------------------------------------
// The admin queue — ROADMAP.md §5.5
// ---------------------------------------------------------------------------

/** What the office sees in the queue. The client's view plus who they are. */
export interface AdminJobRequestView extends ClientJobRequestView {
  clientId: string;
  /** Business name when there is one, otherwise the person. */
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;

  /** Full address, for deciding without opening the client record. */
  propertyAddress: string | null;
  /**
   * Which address, as an id — so the approve dialog's crew picker can score the
   * same-area criterion (business-rules.md §4). The client's own view carries
   * only a label; this is the admin's half and the ranking needs a key.
   *
   * Null when the property was retired after the request was made. Distance is
   * then not scored, and the ranking says so rather than guessing.
   */
  propertyId: string | null;
  /** Which checklist a job here would start with. Null means no template. */
  categoryId: string | null;
  categoryName: string | null;

  serviceTypeId: string | null;
  /** So the approve dialog can pre-fill the duration when the client left it blank. */
  serviceDefaultDurationMinutes: number | null;

  reviewedByName: string | null;
}

export const adminJobRequestListQuerySchema = listQuerySchema.extend({
  sort: sortParam(RECORD_SORTS, 'newest'),

  status: z.enum([...JOB_REQUEST_STATUS_VALUES, 'all']).default('pending'),
});

export type AdminJobRequestListQuery = z.infer<typeof adminJobRequestListQuerySchema>;

/**
 * Approving: this is a job being created, so it carries everything a job needs.
 *
 * The client proposed a date, a time, a service and maybe a duration; the office
 * confirms or changes each one, and adds the crew. Nothing is inherited
 * silently — an approval that quietly used a preferred date the office never
 * looked at is how a cleaner ends up somewhere nobody expected.
 */
export const approveJobRequestSchema = z.object({
  serviceTypeId: z.uuid('Choose the service this job is'),
  categoryId: z.uuid().nullable().optional(),

  scheduledDate: calendarDateSchema,
  scheduledTime: wallClockTimeSchema,
  estimatedDurationMinutes: z
    .number()
    .int('Give whole minutes')
    .min(15, 'A job takes at least 15 minutes')
    .max(1440, 'A job cannot run longer than a day'),

  employeeIds: z.array(z.uuid()).max(20).optional(),

  specialInstructions: z
    .string()
    .trim()
    .max(2000)
    .transform((value) => (value.length === 0 ? null : value))
    .nullable()
    .optional(),

  /**
   * **The client reads this**, on an approval as much as on a decline. Optional
   * here and required on a decline — see below.
   */
  reviewNote: optionalNote,
});

export type ApproveJobRequestInput = z.infer<typeof approveJobRequestSchema>;

/**
 * Declining, and **the note is required** (P16).
 *
 * Two of the four declines in the prototype reach the client with no
 * explanation at all, because the note field existed and was optional. It is not
 * optional here, and the message says what the note is for rather than
 * "required".
 */
export const declineJobRequestSchema = z.object({
  reviewNote: z
    .string()
    .trim()
    .min(1, 'Tell the client why — they see this, and a decline with no reason is a phone call')
    .max(2000, 'Must be 2000 characters or fewer'),
});

export type DeclineJobRequestInput = z.infer<typeof declineJobRequestSchema>;
